import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { FileText, Search, Eye } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export const DiagnosisRecords = () => {
  const navigate = useNavigate();
  const [diagnoses, setDiagnoses] = useState([]);
  const [pets, setPets] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [diagResp, petsResp, ownersResp] = await Promise.all([
        api.get('/visit-diagnoses'),
        api.get('/pets'),
        api.get('/owners'),
      ]);
      setDiagnoses(diagResp.data);
      setPets(petsResp.data);
      setOwners(ownersResp.data);
    } catch (error) {
      toast.error('Failed to load diagnosis records');
    } finally {
      setLoading(false);
    }
  };

  const getPet = (petId) => pets.find((p) => p.id === petId);
  const getOwnerName = (ownerId) => owners.find((o) => o.id === ownerId)?.full_name || '—';

  const filtered = diagnoses.filter((d) => {
    const pet = getPet(d.pet_id);
    const q = search.toLowerCase();
    return (
      d.diagnosis_name.toLowerCase().includes(q) ||
      (d.description || '').toLowerCase().includes(q) ||
      pet?.name?.toLowerCase().includes(q) ||
      getOwnerName(pet?.owner_id).toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading diagnosis records...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-heading font-bold text-sage-800 mb-2">Diagnosis Records</h1>
          <p className="text-gray-600">All diagnosis records across visits</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by diagnosis, pet, owner..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-xl"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-lg p-12 text-center text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          {search ? 'No records match your search.' : 'No diagnosis records found.'}
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
          {/* Mobile card view */}
          <div className="md:hidden divide-y divide-gray-100">
            {filtered.map((d) => {
              const pet = getPet(d.pet_id);
              return (
                <div key={d.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-sage-800">{pet?.name || '—'}</p>
                      <p className="text-sm text-gray-500">{getOwnerName(pet?.owner_id)}</p>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-full flex-shrink-0" onClick={() => navigate(`/dashboard/visits/${d.visit_id}`)}>
                      <Eye className="w-4 h-4 mr-1" />Visit
                    </Button>
                  </div>
                  <p className="text-sm font-medium text-gray-800">{d.diagnosis_name}</p>
                  {d.description && <p className="text-xs text-gray-400 mt-1">{d.description}</p>}
                  <p className="text-xs text-gray-400 mt-1">{new Date(d.created_at).toLocaleDateString()}</p>
                </div>
              );
            })}
          </div>
          {/* Desktop table view */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pet</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Diagnosis</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((d) => {
                  const pet = getPet(d.pet_id);
                  return (
                    <TableRow key={d.id} className="hover:bg-sage-50 transition-colors">
                      <TableCell className="font-semibold text-sage-800">{pet?.name || '—'}</TableCell>
                      <TableCell className="text-gray-600">{getOwnerName(pet?.owner_id)}</TableCell>
                      <TableCell className="font-medium">{d.diagnosis_name}</TableCell>
                      <TableCell className="text-gray-500 text-sm max-w-xs truncate">{d.description || '—'}</TableCell>
                      <TableCell className="text-gray-500 text-sm">{new Date(d.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => navigate(`/dashboard/visits/${d.visit_id}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Visit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};
